import * as Excel from 'exceljs/dist/exceljs.min.js';
import { saveAs } from 'file-saver';
import { JobPricingCustomGrade } from 'helpers/models/JobPricingDetailModels';
import moment from 'moment';
import { getDataUrlForImageUrl } from 'utilities/image.utils';
import logoKF from '../../../../assets/images/KF-logo_white_small.png';
let workbook: any = null;

const createExcelWorkbook = () => {
  return new Excel.Workbook();
};

const exportExcel = (fileName: string) => {
  workbook.xlsx.writeBuffer().then((data: any) => {
    const blob = new Blob([data], { type: '' });
    saveAs(blob, fileName);
    workbook = null;
  });
};

const addCoverPage = (coverData: any) => {
  const sheet = workbook.addWorksheet(coverData.cover);

  sheet.columns = [
    { key: 'col1', width: 19 },
    { key: 'col2', width: 13.5 },
    { key: 'col3', width: 10.5 },
    { key: 'col4', width: 25 },
    { key: 'col5', width: 74.5 },
    { key: 'col6', width: 8 },
  ];

  const row = {
    col1: ' ',
    col2: ' ',
    col3: ' ',
    col4: ' ',
    col5: ' ',
    col6: ' ',
  };

  sheet.addRow(row);
  sheet.addRow({
    ...row,
    col2: coverData.organizationalPerformance,
  });
  sheet.addRow(row);
  sheet.addRow({ ...row, col2: coverData.rewardBenchmarking });
  sheet.addRow({
    ...row,
    col2: coverData.marketData + ' - ' + coverData.singleJobPricing,
  });
  sheet.addRow(row);
  sheet.addRow(row);
  sheet.addRow({
    ...row,
    col2: coverData.company,
    col3: coverData.companyName,
  });
  sheet.addRow({
    ...row,
    col2: coverData.country,
    col3: coverData.selectedCountry + coverData.selectedCurrency,
  });
  sheet.addRow({
    ...row,
    col2: coverData.date,
    col3: moment().format('DD MMMM YYYY'),
  });
  sheet.addRow(row);
  sheet.addRow(row);
  sheet.addRow(row);
  sheet.addRow({
    ...row,
    col5: '\u00A9 ' + coverData.copyRight,
  });
  sheet.addRow(row);

  const blueBg = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'ff004E5B' },
  };
  const grayBg = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'ffD6D9D7' },
  };
  const blackBg = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'ff000000' },
  };
  const defaultFont = { name: 'Arial', size: 10, bold: true };

  const rowHeights = [78, 33, 58, 36, 26, 177, 28, 15, 15, 15, 38, 15, 24, 23, 19];

  // Add bg colors and default font styles
  Array(6)
    .fill(null)
    .forEach((_, index) => {
      const r = sheet.getRow(index + 1);
      r.fill = blueBg;
      r.font = defaultFont;
      r.height = rowHeights[index];
    });
  Array(5)
    .fill(null)
    .forEach((_, index) => {
      const r = sheet.getRow(index + 7);
      r.fill = grayBg;
      r.font = defaultFont;
      r.height = rowHeights[index + 6];
    });
  Array(4)
    .fill(null)
    .forEach((_, index) => {
      const r = sheet.getRow(index + 12);
      r.fill = blackBg;
      r.font = defaultFont;
      r.height = rowHeights[index + 11];
    });

  // Add individual cell styles
  sheet.getCell('B2').font = { ...defaultFont, color: { argb: 'ffffffff' } };
  sheet.getCell('B4').font = {
    ...defaultFont,
    color: { argb: 'ffffffff' },
    size: 24,
  };
  sheet.getCell('B5').font = {
    ...defaultFont,
    color: { argb: 'ffffffff' },
    size: 18,
  };
  sheet.getCell('C8').font = { ...defaultFont, bold: false };
  sheet.getCell('C9').font = { ...defaultFont, bold: false };
  sheet.getCell('C10').font = { ...defaultFont, bold: false };
  sheet.getCell('E14').font = {
    ...defaultFont,
    color: { argb: 'ffffffff' },
    bold: false,
  };
  sheet.getCell('E14').alignment = { horizontal: 'right', vertical: 'top' };

  // Merge cells
  sheet.mergeCells('B2:E2');
  sheet.mergeCells('B4:E4');
  sheet.mergeCells('B5:E5');
  sheet.mergeCells('C8:E8');
  sheet.mergeCells('C9:E9');
  sheet.mergeCells('C10:E10');

  // Add image
  return getDataUrlForImageUrl(logoKF).then((dataUrl: any) => {
    const image = workbook.addImage({
      base64: dataUrl,
      extension: 'png',
    });
    sheet.addImage(image, {
      tl: { col: 0.5, row: 12.5 },
      ext: { width: 250, height: 54 },
    });
  });
};

const addCriteriaPage = (criteriaData: any) => {
  const sheet = workbook.addWorksheet(criteriaData.analysisCriteria);
  sheet.views = [{ showGridLines: false }];
  sheet.columns = [
    { key: 'col1', width: 50 },
    { key: 'col2', width: 100 },
  ];
  const row = { col1: ' ', col2: ' ' };
  sheet.addRow({ ...row, col1: criteriaData.analysisCriteria.toUpperCase() });
  sheet.addRow(row);
  sheet.addRow(row);
  sheet.addRow({ ...row, col1: criteriaData.marketCriteria.toUpperCase() });
  const defaultFont = { name: 'Arial', size: 10, bold: true };
  const defaultAlignment = { horizontal: 'left' };
  const blueBg = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'ffC7E6E8' },
  };
  const cellBorder = {
    top: { style: 'thin', color: { argb: 'c0c0c0' } },
    left: { style: 'thin', color: { argb: 'c0c0c0' } },
    bottom: { style: 'thin', color: { argb: 'c0c0c0' } },
    right: { style: 'thin', color: { argb: 'c0c0c0' } },
  };
  const pushRows = (values: string[], label: string) =>
    values.forEach((v, i) =>
      sheet.addRow({
        col1: !i ? label : '',
        col2: v,
      }),
    );
  for (const dataCriteria of criteriaData.dataCriteria) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    pushRows(dataCriteria.values, dataCriteria.label);
  }
  sheet.getColumn(1).font = defaultFont;
  sheet.getColumn(2).font = { ...defaultFont, bold: false };
  sheet.getColumn(1).alignment = defaultAlignment;
  sheet.getColumn(2).alignment = defaultAlignment;
  sheet.getRow(1).font = { ...defaultFont, size: 20 };
  sheet.eachRow((row: any) => {
    row.eachCell((cell: any, colNumber: any) => {
      if (
        cell._address !== 'A1' &&
        cell._address !== 'B1' &&
        cell._address !== 'A2' &&
        cell._address !== 'B2' &&
        cell._address !== 'A3' &&
        cell._address !== 'B3'
      ) {
        sheet.getCell(cell._address).fill = blueBg;
        if (colNumber >= 1 && colNumber <= 5) {
          cell.border = cellBorder;
        }
      }
    });
  });
  sheet.mergeCells('A1:B1');
};

const addDataPage = (tableData: any) => {
  const sheet = workbook.addWorksheet(tableData.jobPricing);
  const defaultAlignment = { horizontal: 'left' };
  const colArr = [];
  sheet.views = [{ showGridLines: false }];
  sheet.columns = [{ key: 'col1', width: 60 }];
  for (let i = 1; i <= tableData.tableHeaderList.length; i += 1) {
    const cols: any = {};
    cols.key = 'col' + i + 1;
    cols.width = 30;
    cols.alignment = defaultAlignment;
    colArr.push(cols);
  }
  sheet.columns = sheet.columns.concat(colArr);
  const emptyRow: any = {};
  for (let i = 1; i <= tableData.tableHeaderList.length; i += 1) {
    emptyRow['col' + i] = ' ';
  }
  const row = { col1: ' ', col2: ' ' };
  const defaultFont = { name: 'Arial', size: 10, bold: true };
  const grayBg = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'D7DBDD' },
  };
  const blueBg = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'c7e6e8' },
  };
  const cellBorder = {
    top: { style: 'thin', color: { argb: 'c0c0c0' } },
    left: { style: 'thin', color: { argb: 'c0c0c0' } },
    bottom: { style: 'thin', color: { argb: 'c0c0c0' } },
    right: { style: 'thin', color: { argb: 'c0c0c0' } },
  };
  sheet.addRow({ ...row, col1: tableData.jobPricing.toUpperCase() });
  sheet.getRow(1).font = { ...defaultFont, size: 20 };
  sheet.addRow(emptyRow);
  sheet.addRow(tableData.tableHeaderList);
  const headerRow = sheet.getRow(3);
  for (let x = 1; x <= tableData.tableHeaderList.length; x += 1) {
    headerRow.getCell(x).fill = blueBg;
    headerRow.getCell(x).font = { ...defaultFont, height: 15 };
    headerRow.getCell(x).border = cellBorder;
  }
  for (let y = 0; y < tableData.tableDetailsList.length; y += 1) {
    const rowDetails = tableData.tableDetailsList[y];
    sheet.addRow(rowDetails);
    const dataRow = sheet.getRow(3 + y + 1);
    for (let z = 1; z <= rowDetails.length; z += 1) {
      dataRow.getCell(z).fill = grayBg;
      dataRow.getCell(z).font = {
        ...defaultFont,
        bold: false,
        width: 10,
      };
      dataRow.getCell(z).alignment = defaultAlignment;
      dataRow.getCell(z).border = cellBorder;
    }
  }
};

const addDefinitionsPage = (definitionsData: any) => {
  const row = { col1: ' ', col2: ' ', col3: ' ' };
  const defaultFont = { name: 'Arial', size: 10, bold: true };
  const wrapText = { wrapText: true };
  const cellBorder = {
    top: { style: 'dotted', bold: false },
    left: { style: 'dotted', bold: false },
    bottom: { style: 'dotted', bold: false },
    right: { style: 'dotted', bold: false },
  };
  const sheet = workbook.addWorksheet(definitionsData.definitions);
  sheet.views = [{ showGridLines: false }];
  sheet.columns = [
    { key: 'col1', width: 5 },
    { key: 'col2', width: 60 },
    { key: 'col3', width: 100 },
  ];
  sheet.alignment = wrapText;
  sheet.getColumn(2).font = { ...defaultFont };
  sheet.getColumn(3).font = { ...defaultFont, bold: false };
  sheet.addRow({
    ...row,
    col2: definitionsData.definitions.toUpperCase(),
  });
  sheet.addRow(row);
  sheet.addRow({
    ...row,
    col2: definitionsData.generalDefinitions.toUpperCase(),
  });
  sheet.getCell('B1').font = { ...defaultFont, size: 20 };
  sheet.getCell('B3').font = { ...defaultFont, size: 10 };
  for (const element of definitionsData.definitionTextList) {
    sheet.addRow({
      ...row,
      col2: element.defKey,
      col3: element.defValue || '-',
    });
  }
  definitionsData.referenceLevelsList.forEach((grade: JobPricingCustomGrade) =>
    sheet.addRow({
      ...row,
      col2: grade.low + '-' + grade.middle + '-' + grade.high,
      col3: grade.name,
    }),
  );
  sheet.eachRow((row: any) => {
    row.eachCell((cell: any, colNumber: any) => {
      if (
        cell._address !== 'B1' &&
        cell._address !== 'C1' &&
        cell._address !== 'B2' &&
        cell._address !== 'C2' &&
        cell._address !== 'B3' &&
        cell._address !== 'C3' &&
        colNumber >= 1 &&
        colNumber <= 15
      ) {
        cell.border = cellBorder;
        cell.alignment = wrapText;
      }
    });
  });
};

export const downloadExcelExportReport = async (excelDocDetails: any, fileName: string) => {
  workbook = createExcelWorkbook();
  await addCoverPage(excelDocDetails.coverData);
  addCriteriaPage(excelDocDetails.criteriaData);
  addDataPage(excelDocDetails.tableData);
  addDefinitionsPage(excelDocDetails.definitionsData);
  exportExcel(fileName);
};
